<template>
  <div class="rec-account-name-select">
    <input
      ref="inputRef"
      :value="value"
      type="text"
      class="form-control form-control-sm"
      :placeholder="placeholder"
      :disabled="disabled"
      autocomplete="off"
      @input="onInput($event.target.value)"
      @focus="open"
      @blur="close"
      @keydown.esc="close"
    />
    <ul v-if="isOpen" class="rec-account-name-select__list" :style="listStyle">
      <li
        v-for="option in filteredOptions"
        :key="option.account_name"
        @mousedown.prevent="select(option.account_name)"
      >
        {{ option.account_name }}
        <small v-if="!option.in_cashbook" class="text-muted">(not in cash book)</small>
      </li>
      <li v-if="showNewOption" class="rec-account-name-select__new" @mousedown.prevent="select(typedValue)">
        + New: "{{ typedValue }}"
      </li>
      <li v-if="!filteredOptions.length && !showNewOption" class="rec-account-name-select__hint">
        No cash book Account Names for this UMR - type to add a new one
      </li>
    </ul>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onBeforeUnmount } from '@vue/composition-api'

// Mirrors dbo.fn_FCP_Norm, so "Acme Ltd" and "ACME LTD " count as the same name.
const normalise = (value) =>
  String(value || '')
    .replace(/[ \t\r\n]/g, ' ')
    .trim()
    .toUpperCase()

// LIQ-2571: Account Name type-ahead for one row of RecAccountNameReconciliationPanel. Same
// behaviour as BDX Match's dropdown (LIQ-2410) - pick a name for the key's UMR or type a new one -
// but fed the panel's own list ([{ account_name, in_cashbook }]), whose cash book names leave out
// the liquidity-only ones the rec report ignores. Kept separate from BDX Match's component on
// purpose so the rec panel doesn't change LIQ-2410's code.
//
// The list is position: fixed under the input, not absolute: the panel's table scrolls, and an
// absolute list would be clipped at its bottom rows. Any scroll closes it rather than leaving it
// floating away from its input.
export default defineComponent({
  name: 'rec-account-name-select',
  props: {
    value: String,
    options: {
      type: Array,
      default: () => []
    },
    disabled: Boolean,
    placeholder: {
      type: String,
      default: 'Select or type an Account Name'
    }
  },
  emits: ['input'],
  setup(props, { emit }) {
    const inputRef = ref(null)
    const isOpen = ref(false)
    const listStyle = ref({})

    const typedValue = computed(() => String(props.value || '').trim())

    const filteredOptions = computed(() => {
      const query = typedValue.value.toLowerCase()
      if (!query) return props.options
      return props.options.filter((option) => option.account_name.toLowerCase().includes(query))
    })

    const showNewOption = computed(() => {
      const typed = normalise(typedValue.value)
      return !!typed && !props.options.some((option) => normalise(option.account_name) === typed)
    })

    const onScroll = () => close()

    const open = () => {
      const rect = inputRef.value && inputRef.value.getBoundingClientRect()
      if (!rect) return
      listStyle.value = { top: `${rect.bottom + 2}px`, left: `${rect.left}px`, minWidth: `${rect.width}px` }
      isOpen.value = true
      window.addEventListener('scroll', onScroll, true)
    }

    const close = () => {
      isOpen.value = false
      window.removeEventListener('scroll', onScroll, true)
    }

    const onInput = (newValue) => {
      emit('input', newValue)
      if (!isOpen.value) open()
    }

    const select = (accountName) => {
      emit('input', accountName)
      close()
    }

    onBeforeUnmount(() => window.removeEventListener('scroll', onScroll, true))

    return { inputRef, isOpen, listStyle, typedValue, filteredOptions, showNewOption, open, close, onInput, select }
  }
})
</script>

<style scoped>
/* Same look as BDX Match's Account Name dropdown (BDXEditFormAccountName). */
.rec-account-name-select__list {
  position: fixed;
  z-index: 1080;
  width: max-content;
  max-width: 420px;
  margin: 0;
  padding: 4px 0;
  list-style: none;
  background-color: #fff;
  border: 1px solid #e3e8ec;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  max-height: 200px;
  overflow-x: hidden;
  overflow-y: auto;
}

.rec-account-name-select__list li {
  padding: 4px 10px;
  /* Matches the rec page's table text (RecProductionReport.scss: 0.7rem). */
  font-size: 0.7rem;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rec-account-name-select__list li:hover {
  background-color: #f1f3f5;
  color: #1c4a50;
}

.rec-account-name-select__list li.rec-account-name-select__new {
  border-top: 1px solid #e3e8ec;
  font-weight: 500;
}

.rec-account-name-select__list li.rec-account-name-select__hint {
  cursor: default;
  color: #6c757d;
  white-space: normal;
}

.rec-account-name-select__list li.rec-account-name-select__hint:hover {
  background-color: transparent;
}
</style>
